/**
 * /customer/reward/:id — single reward detail + redemption flow (Phase 2c).
 *
 * Mirrors mobile/(customer)/reward/[id].tsx in flow + copy:
 *   1. Hero image, brand row, expiry, title, description.
 *   2. "Your progress" box (current / required + bar + status text).
 *   3. T&C + branches sections.
 *   4. Primary CTA → confirmation modal → POST redeem → on success
 *      invalidate `qk.customerCards()` and reveal a success card with
 *      the redemption code + expiry.
 *
 * Idempotency safety (Mandate from operator dispatch):
 *   - `request_id` is generated ONCE per page lifecycle via
 *     `useRef(crypto.randomUUID())`. Retries reuse the same id so the
 *     backend's redemption_idempotency cache hits with `cached:true`
 *     instead of double-debiting stamps.
 *   - A new id is minted only when the user explicitly closes the
 *     confirmation/result and starts a fresh attempt.
 *
 * D4 — non-joined access:
 *   When `myRewards` doesn't contain :id (user hasn't joined that brand)
 *   we fall back to `/api/brands/:brand_id/rewards` if a `?brand=`
 *   query param is supplied. The CTA flips to "Join {Brand} to redeem"
 *   so direct URL navigation never silently fails.
 */
import { useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, Gift, Lock, CheckCircle2, Sparkles, Star, MapPin,
  Loader2, X, Copy, Clock,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

/** RFC4122-ish v4 UUID with a `crypto.randomUUID` fast-path. */
function newRequestId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  const hex = (n) => n.toString(16).padStart(2, '0');
  const buf = new Uint8Array(16);
  for (let i = 0; i < 16; i += 1) buf[i] = Math.floor(Math.random() * 256);
  buf[6] = (buf[6] & 0x0f) | 0x40;
  buf[8] = (buf[8] & 0x3f) | 0x80;
  const b = Array.from(buf, hex).join('');
  return `${b.slice(0, 8)}-${b.slice(8, 12)}-${b.slice(12, 16)}-${b.slice(16, 20)}-${b.slice(20)}`;
}

function formatExpiryDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { year: 'numeric', month: 'short' });
}

function formatExpiresAt(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

export default function CustomerRewardDetailPage() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const brandQueryId = searchParams.get('brand');
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const qc = useQueryClient();

  // ---- Stable request_id ------------------------------------------------
  // The id ref is reset only when the user explicitly closes the success
  // card and triggers a new redemption attempt (see `resetForRetry`).
  const requestIdRef = useRef(newRequestId());

  // ---- Data loading -----------------------------------------------------
  //
  // Primary source: `customerMyRewards()` — covers every joined-brand
  // reward with full `status` + `progress` fields.
  //
  // D4 fallback (unjoined brand):
  //   When `myRewards` doesn't carry :id we need to locate the reward
  //   by some other route. The page must work for both URL shapes:
  //
  //     (a) `/customer/reward/:id?brand=:brand_id`
  //         → single brand probe via `customerBrandRewards`.
  //     (b) `/customer/reward/:id`            (bare URL — no hint)
  //         → enumerate active brands, then probe each non-joined
  //           brand's `/api/brands/:id/rewards` until we hit a match.
  //
  // Both paths land on the same `customerBrandRewards` endpoint so the
  // backend's customer-visible filter (`utils.visibility.customer_visible_rewards_filter`)
  // continues to gate inactive brands / soft-deleted rewards.
  // ---------------------------------------------------------------------
  const myRewardsQ = useQuery({
    queryKey: qk.customerMyRewards(),
    queryFn: () => api.customerMyRewards(token),
    enabled: !!token,
  });

  const myReward = useMemo(
    () => (myRewardsQ.data ?? []).find((r) => r.id === id) ?? null,
    [myRewardsQ.data, id],
  );

  // `myResolved` is the gate that opens the fallback. We only enter
  // probe mode once `customerMyRewards` has returned and confirmed
  // the reward isn't joined-side.
  const myResolved = !!token && !myRewardsQ.isLoading && !myReward;

  // Fast path: single-brand probe when `?brand=` is supplied.
  const singleBrandProbeQ = useQuery({
    queryKey: qk.customerBrandRewards(brandQueryId ?? '__none__'),
    queryFn: () => api.customerBrandRewards(token, brandQueryId),
    enabled: myResolved && !!brandQueryId,
  });
  const singleBrandReward = useMemo(
    () => (singleBrandProbeQ.data ?? []).find((r) => r.id === id) ?? null,
    [singleBrandProbeQ.data, id],
  );

  // Slow path: when no `?brand=` is supplied we enumerate brands the
  // customer has NOT joined (joined ones are already covered by
  // `myRewardsQ`) and probe each one's rewards endpoint in parallel.
  // Concurrency cap is implicit (handful of brands platform-wide).
  const brandsListQ = useQuery({
    queryKey: qk.customerBrands(),
    queryFn: () => api.customerBrands(token),
    enabled: myResolved && !brandQueryId,
  });
  const cardsQ = useQuery({
    queryKey: qk.customerCards(),
    queryFn: () => api.customerCards(token),
    enabled: myResolved && !brandQueryId,
  });
  const unjoinedBrandIds = useMemo(() => {
    if (brandQueryId) return [];
    const joinedSet = new Set(
      (cardsQ.data ?? [])
        .map((c) => c?.brand?.id)
        .filter(Boolean),
    );
    return (brandsListQ.data ?? [])
      .map((b) => b.id)
      .filter((bid) => bid && !joinedSet.has(bid));
  }, [brandQueryId, brandsListQ.data, cardsQ.data]);

  const multiProbeQ = useQuery({
    queryKey: ['customer-reward-multi-probe', id, unjoinedBrandIds],
    // Iterate every unjoined brand's rewards in parallel and return
    // the first hit. We tolerate per-brand 404s — the goal is to find
    // ANY brand that carries this reward id.
    queryFn: async () => {
      const lists = await Promise.all(
        unjoinedBrandIds.map((bid) =>
          api
            .customerBrandRewards(token, bid)
            .then((list) => ({ bid, list }))
            .catch(() => ({ bid, list: [] })),
        ),
      );
      for (const { list } of lists) {
        const hit = list.find((r) => r.id === id);
        if (hit) return hit;
      }
      return null;
    },
    enabled:
      myResolved &&
      !brandQueryId &&
      brandsListQ.isSuccess &&
      cardsQ.isSuccess &&
      unjoinedBrandIds.length > 0,
  });

  // The canonical reward record the page renders against. Prefer
  // myReward (joined — carries status + progress); fall back to
  // either probe path. `isJoined` is the single source-of-truth for
  // the CTA branch and the progress lockup.
  const reward = myReward || singleBrandReward || multiProbeQ.data || null;
  const isJoined = !!myReward;

  // ---- Redemption state -------------------------------------------------
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // server response on success
  const [errorMessage, setErrorMessage] = useState('');

  const onRedeemConfirmed = async () => {
    if (!reward || !token || submitting) return;
    setSubmitting(true);
    setErrorMessage('');
    try {
      const res = await api.customerRedeem(token, reward.id, requestIdRef.current);
      setResult(res);
      setConfirmOpen(false);
      // Cards must refetch so the new current_stamps + last_activity_at
      // land everywhere (CardsPage, CardDetailPage, HistoryPage, Home).
      await Promise.all([
        qc.invalidateQueries({ queryKey: qk.customerCards() }),
        qc.invalidateQueries({ queryKey: qk.customerMyRewards() }),
        qc.invalidateQueries({ queryKey: ['customer-recent-activity'] }),
        qc.invalidateQueries({ queryKey: ['customer-transactions-history'] }),
      ]);
      toast.success('Reward redeemed', {
        description: `Code ${res.redemption_code} — show it at the counter.`,
      });
    } catch (e) {
      const code = e?.raw?.detail?.code ?? null;
      let msg = e?.detail ?? e?.message ?? 'Something went wrong.';
      if (code === 'concurrency_conflict') {
        msg = 'Card state changed elsewhere — please refresh.';
        await qc.invalidateQueries({ queryKey: qk.customerMyRewards() });
        await qc.invalidateQueries({ queryKey: qk.customerCards() });
      } else if (code === 'insufficient_stamps') {
        msg = 'Not enough stamps yet — earn a few more.';
      } else if (code === 'reward_expired') {
        msg = 'This reward has expired.';
      } else if (code === 'card_not_joined') {
        msg = 'Join this brand before redeeming.';
      }
      setErrorMessage(typeof msg === 'string' ? msg : 'Something went wrong.');
      toast.error('Could not redeem', { description: msg });
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const resetForRetry = () => {
    // Mint a new request_id only when the user actively starts over —
    // any in-flight retry on the previous attempt MUST keep the old id.
    requestIdRef.current = newRequestId();
    setResult(null);
    setErrorMessage('');
  };

  // ---- Loading / 404 ----------------------------------------------------
  // The page is "still loading" if either:
  //   (1) the primary `customerMyRewards` is in flight, or
  //   (2) we've moved into a fallback probe and that probe is fetching
  //       (single-brand path when `?brand=` is supplied; multi-brand
  //       enumeration when it isn't).
  // We treat the brands list + cards list as part of the multi-probe
  // gate to avoid a flash of "Reward not found" between when
  // `myRewardsQ` resolves and when the multi-probe actually starts.
  const inSingleBrandProbe = myResolved && !!brandQueryId;
  const inMultiBrandProbe = myResolved && !brandQueryId;
  const isLoading =
    myRewardsQ.isLoading ||
    (inSingleBrandProbe && singleBrandProbeQ.isLoading) ||
    (inMultiBrandProbe &&
      (brandsListQ.isLoading ||
        cardsQ.isLoading ||
        multiProbeQ.isLoading ||
        // We also have to wait while there's at least one unjoined
        // brand to probe and the probe hasn't settled yet.
        (unjoinedBrandIds.length > 0 && !multiProbeQ.isFetched)));

  if (isLoading) {
    return (
      <div
        className="flex items-center justify-center min-h-[40vh] text-white/45"
        data-testid="customer-reward-detail-screen"
      >
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  }
  if (!reward) {
    return (
      <div
        className="rounded-3xl border border-white/8 bg-[#15151B] p-10 text-center"
        data-testid="customer-reward-detail-screen"
      >
        <Gift size={28} className="mx-auto text-gold/55 mb-3" />
        <div className="text-base font-medium">Reward not found</div>
        <div className="text-xs text-white/45 mt-1 mb-5">
          The reward you opened may have been removed or is no longer available.
        </div>
        <Link
          to="/customer/rewards"
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gold/15 border border-gold/45 text-gold text-sm font-medium hover:bg-gold/20 transition-colors"
        >
          Back to Rewards
        </Link>
      </div>
    );
  }

  // ---- Derive UI values -------------------------------------------------
  const required = reward.progress?.required ?? reward.stamps_required;
  const current = reward.progress?.current ?? 0;
  const remaining = Math.max(0, required - current);
  const isAvailable = reward.status === 'available';
  const pct = Math.min(100, Math.round((current / Math.max(1, required)) * 100));
  const tint = reward.brand_primary_color || '#C9A84C';

  return (
    <div className="space-y-6" data-testid="customer-reward-detail-screen">
      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/customer/rewards')}
        data-testid="customer-reward-detail-back"
        className="inline-flex items-center gap-1 text-xs font-mono uppercase tracking-wider text-white/60 hover:text-gold transition-colors"
      >
        <ChevronLeft size={14} /> Rewards
      </button>

      {/* Hero */}
      <div
        className="rounded-3xl overflow-hidden border border-white/8"
        style={
          reward.image_url
            ? {
                backgroundImage: `url(${resolveImageUrl(reward.image_url)})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : { background: `linear-gradient(135deg, ${tint}55 0%, #0A0A0A 100%)` }
        }
      >
        <div className="bg-gradient-to-t from-black/95 via-black/45 to-black/15 px-6 pt-32 pb-6">
          <div className="flex items-center gap-3 mb-4">
            {reward.brand_logo_url ? (
              <img
                src={resolveImageUrl(reward.brand_logo_url)}
                alt=""
                className="w-12 h-12 rounded-2xl border border-white/15 shadow-xl bg-black/40 shrink-0"
              />
            ) : null}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold truncate">
                {reward.brand_name}
              </div>
              {reward.expiry_date ? (
                <div className="text-[11px] text-white/55 mt-0.5">
                  Expires {formatExpiryDate(reward.expiry_date)}
                </div>
              ) : null}
            </div>
            <div className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gold/10 border border-gold/30 text-gold text-xs font-semibold shrink-0">
              <Star size={12} />
              {reward.stamps_required} stamps
            </div>
          </div>

          <div className="font-display text-2xl md:text-3xl font-bold leading-tight">
            {reward.title}
          </div>
          {reward.description ? (
            <p className="text-sm text-white/65 mt-2 leading-relaxed max-w-2xl">
              {reward.description}
            </p>
          ) : null}
        </div>
      </div>

      {/* Progress box (only when status/progress is known — i.e. joined) */}
      {reward.progress ? (
        <div
          className="rounded-2xl border border-gold/30 bg-gold/[0.06] px-5 py-5"
          data-testid="customer-reward-detail-progress"
        >
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gold">
              Your progress
            </div>
            <div className="text-xl font-display font-bold">
              <span style={{ color: tint }}>
                {Math.min(current, required)}
              </span>
              <span className="text-white/35"> / {required}</span>
            </div>
          </div>
          <div className="h-2 rounded-full bg-white/10 overflow-hidden mt-3">
            <div
              className="h-full rounded-full transition-all"
              style={{ width: `${pct}%`, backgroundColor: tint }}
            />
          </div>
          {isAvailable ? (
            <div className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-gold">
              <CheckCircle2 size={14} />
              Ready to redeem
            </div>
          ) : (
            <div className="mt-3 text-sm text-white/65">
              {remaining} more stamp{remaining === 1 ? '' : 's'} to unlock
            </div>
          )}
        </div>
      ) : null}

      {/* T&C */}
      {reward.terms_and_conditions ? (
        <section data-testid="customer-reward-detail-terms">
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gold mb-2">
            Terms &amp; conditions
          </div>
          <p className="text-sm text-white/65 leading-relaxed">
            {reward.terms_and_conditions}
          </p>
        </section>
      ) : null}

      {/* Branches */}
      {(reward.available_branch_names ?? []).length > 0 ? (
        <section data-testid="customer-reward-detail-branches">
          <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-gold mb-2">
            Available at
          </div>
          <div className="flex flex-wrap gap-2">
            {reward.available_branch_names.map((n) => (
              <span
                key={n}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#15151B] border border-white/10 text-xs text-white/75"
              >
                <MapPin size={12} className="text-gold" />
                {n}
              </span>
            ))}
          </div>
        </section>
      ) : null}

      {/* CTA — varies by joined state + status */}
      <div className="pt-2">
        {!isJoined ? (
          <Link
            to={`/customer/brand/${reward.brand_id}`}
            data-testid="customer-reward-detail-join-cta"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gold/15 border border-gold/45 text-gold text-sm font-semibold hover:bg-gold/25 transition-colors w-full sm:w-auto sm:min-w-[260px]"
          >
            <Sparkles size={16} />
            Join {reward.brand_name} to redeem
          </Link>
        ) : isAvailable ? (
          <button
            type="button"
            onClick={() => {
              setErrorMessage('');
              setConfirmOpen(true);
            }}
            disabled={submitting}
            data-testid="customer-reward-detail-redeem-cta"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gold text-black text-sm font-semibold hover:brightness-110 transition-all w-full sm:w-auto sm:min-w-[260px] disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Sparkles size={16} />
            Redeem · {reward.stamps_required} stamps
          </button>
        ) : (
          <button
            type="button"
            disabled
            data-testid="customer-reward-detail-locked-cta"
            className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-white/5 border border-white/12 text-white/55 text-sm font-medium w-full sm:w-auto sm:min-w-[260px] cursor-not-allowed"
          >
            <Lock size={16} />
            {remaining} more stamp{remaining === 1 ? '' : 's'} to unlock
          </button>
        )}
      </div>

      {errorMessage ? (
        <div
          className="rounded-2xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200"
          data-testid="customer-reward-detail-error"
        >
          {errorMessage}
        </div>
      ) : null}

      {/* Confirmation modal */}
      {confirmOpen ? (
        <ConfirmModal
          reward={reward}
          current={current}
          submitting={submitting}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onRedeemConfirmed}
        />
      ) : null}

      {/* Success result card (post-redeem) */}
      {result ? (
        <SuccessModal
          result={result}
          tint={tint}
          onClose={() => {
            resetForRetry();
          }}
        />
      ) : null}
    </div>
  );
}

function ConfirmModal({ reward, current, submitting, onCancel, onConfirm }) {
  // Mirror mobile copy verbatim:
  //   "Use {N} stamps to redeem {title}. You'll have {N} stamp(s) remaining."
  const remainingAfter = Math.max(0, current - reward.stamps_required);
  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      data-testid="customer-reward-confirm-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="w-full max-w-md rounded-3xl bg-[#15151B] border border-gold/30 p-6"
        data-testid="customer-reward-confirm-modal"
      >
        <div className="w-14 h-14 rounded-full bg-gold/15 border border-gold/35 grid place-items-center mx-auto mb-4">
          <Sparkles size={22} className="text-gold" />
        </div>
        <div className="text-center font-display text-xl font-bold">
          Use stamps to redeem?
        </div>
        <div className="text-center text-sm text-white/65 mt-2 leading-relaxed">
          Use {reward.stamps_required} stamps to redeem{' '}
          <span className="text-gold font-semibold">{reward.title}</span>.
          You&apos;ll have {remainingAfter} stamp
          {remainingAfter === 1 ? '' : 's'} remaining.
        </div>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          data-testid="customer-reward-confirm-yes"
          className="w-full mt-5 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-2xl bg-gold text-black text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {submitting ? (
            <>
              <Loader2 size={16} className="animate-spin" /> Redeeming…
            </>
          ) : (
            <>Yes, redeem now</>
          )}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          data-testid="customer-reward-confirm-cancel"
          className="w-full mt-2 inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-white/5 border border-white/12 text-white/75 text-sm font-medium hover:bg-white/10 transition-colors disabled:opacity-60"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

function SuccessModal({ result, tint, onClose }) {
  const code = result.redemption_code ?? result.code;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success('Code copied');
    } catch {
      toast.error('Could not copy');
    }
  };
  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 flex items-center justify-center px-4"
      data-testid="customer-reward-success-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-[#15151B] border border-gold/35 p-6 text-center"
        data-testid="customer-reward-success-modal"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="customer-reward-success-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full grid place-items-center text-white/55 hover:text-white hover:bg-white/8 transition-colors"
        >
          <X size={16} />
        </button>
        <div className="w-14 h-14 rounded-full bg-gold/20 border border-gold/45 grid place-items-center mx-auto mb-4">
          <CheckCircle2 size={24} className="text-gold" />
        </div>
        <div className="font-display text-xl font-bold">Reward unlocked</div>
        <div className="text-sm text-white/65 mt-1">
          Show this code at the counter.
        </div>

        <div
          className="mt-5 rounded-2xl border border-gold/40 bg-gold/[0.06] py-4 px-4"
          data-testid="customer-reward-success-code"
        >
          <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-gold">
            Redemption code
          </div>
          <div
            className="font-mono text-2xl font-bold tracking-[0.18em] mt-1.5"
            style={{ color: tint }}
          >
            {code}
          </div>
        </div>

        <button
          type="button"
          onClick={onCopy}
          data-testid="customer-reward-success-copy"
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/5 border border-white/12 text-white/85 text-xs font-medium hover:bg-gold/10 hover:border-gold/35 hover:text-gold transition-colors"
        >
          <Copy size={12} /> Copy code
        </button>

        {result.expires_at ? (
          <div className="mt-4 inline-flex items-center gap-1.5 text-[11px] text-white/55">
            <Clock size={11} />
            Expires {formatExpiresAt(result.expires_at)}
          </div>
        ) : null}

        {result.card ? (
          <div className="mt-5 pt-4 border-t border-white/8">
            <div className="text-[10px] font-mono uppercase tracking-[0.16em] text-white/45 mb-1">
              Stamps remaining
            </div>
            <div className="font-display text-2xl font-bold">
              <span style={{ color: tint }}>
                {result.card.current_stamps}
              </span>
              <span className="text-white/40">
                {' '}/ {result.card.next_reward_at ?? '—'}
              </span>
            </div>
          </div>
        ) : null}

        <button
          type="button"
          onClick={onClose}
          data-testid="customer-reward-success-done"
          className="w-full mt-5 inline-flex items-center justify-center px-5 py-3 rounded-2xl bg-gold text-black text-sm font-semibold hover:brightness-110 transition-all"
        >
          Done
        </button>
        {/* Phase 2d — operator §7: deep-link from success modal into
            My Redemptions detail. We surface this as a secondary link
            so the primary action (Done) keeps its visual weight. */}
        {result.redemption_id ? (
          <Link
            to={`/customer/redemption/${result.redemption_id}`}
            onClick={onClose}
            data-testid="customer-reward-success-view-redemption"
            className="block mt-3 text-xs font-medium text-white/65 hover:text-gold transition-colors"
          >
            View in My Redemptions →
          </Link>
        ) : null}
      </div>
    </div>
  );
}
